import * as React from "react";
import { PropsWithChildren } from "react";

import { formComponentDidMountEvent } from "../../../Engine/Core/EventManagement/ExternalEvents/ExternalEvents";
import { createHocDisplayName, GetProps } from "../../TypingUtils";
import { useExternalEventEmitter } from "../Contexts/ExternalEventManagerContext";
import { isModelPath, ModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { BindingPath } from "../../../Engine/Core/CoreTypes";

export function withExternalMountingEventReporting<C extends React.ComponentType<GetProps<C>>>(
    WrappedComponent: C
): React.ComponentType<
    JSX.LibraryManagedAttributes<C, PropsWithChildren<GetProps<C>>> & {
        bindingPath: undefined | ModelPath | BindingPath;
    }
> {
    function WithExternalMountingEventReportingWrapper(
        props: JSX.LibraryManagedAttributes<C, PropsWithChildren<GetProps<C>>> & {
            bindingPath: undefined | ModelPath | BindingPath;
        }
    ): JSX.Element {
        const externalEventEmitter = useExternalEventEmitter();

        React.useEffect(() => {
            let id: string | undefined = undefined;
            const bindingPath: ModelPath | BindingPath | undefined = props.bindingPath;
            if (bindingPath != undefined) {
                if (isModelPath(bindingPath)) {
                    id = bindingPath.toLegacyPath();
                } else {
                    id = bindingPath;
                }
            }
            if (id != undefined) {
                externalEventEmitter.triggerExternalEvents([formComponentDidMountEvent(id)]);
            }
        }, []);

        return <WrappedComponent {...props} />;
    }

    WithExternalMountingEventReportingWrapper.displayName = createHocDisplayName(
        WrappedComponent,
        "ExternalMountingEventReporting"
    );
    return WithExternalMountingEventReportingWrapper;
}
